/**
 * The five themes, in the order the picker offers them.
 *
 * A theme is a name and nothing else here: every colour lives in
 * `themes.css`, generated and validated by `scripts/generate-themes.mjs`
 * (ADR 0007). Each has a light and a dark palette; which one shows follows the
 * device's setting, because somebody who reads in dark mode at night should not
 * have to choose a theme to keep it.
 */
export const THEMES = ["kontor", "tanne", "pflaume", "bernstein", "graphit"] as const;

export type Theme = (typeof THEMES)[number];

export const DEFAULT_THEME: Theme = "kontor";

export function isTheme(value: unknown): value is Theme {
  return typeof value === "string" && (THEMES as readonly string[]).includes(value);
}

/**
 * The last theme this tab showed, so a reload paints in it straight away
 * rather than flashing the default until `/me` answers.
 *
 * sessionStorage, never localStorage: the thin-path walkthrough asserts
 * localStorage stays empty, because that is where a persistent XSS would look
 * first. The server is where the choice actually lives; this is only a cache.
 */
const CACHE_KEY = "kontora.theme";

export function cachedTheme(): Theme | undefined {
  try {
    const value = sessionStorage.getItem(CACHE_KEY);
    return isTheme(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  try {
    sessionStorage.setItem(CACHE_KEY, theme);
  } catch {
    // Storage can be unavailable (private windows, blocked site data). The
    // theme still applies; only the flash-free reload is lost.
  }
}

/**
 * Whether the interface follows the device's setting, or is pinned to one of
 * the two palettes regardless of it.
 *
 * Independent of the colour theme: choosing "plum" says which five palettes to
 * use, choosing "dark" says which of the two (light/dark) within it to show.
 */
export const SCHEMES = ["system", "light", "dark"] as const;

export type Scheme = (typeof SCHEMES)[number];

export const DEFAULT_SCHEME: Scheme = "system";

export function isScheme(value: unknown): value is Scheme {
  return typeof value === "string" && (SCHEMES as readonly string[]).includes(value);
}

/**
 * The last scheme this tab showed, so a reload paints in it straight away
 * rather than flashing the default until `/me` answers.
 *
 * sessionStorage, never localStorage — see `cachedTheme` above.
 */
const SCHEME_CACHE_KEY = "kontora.scheme";

export function cachedScheme(): Scheme | undefined {
  try {
    const value = sessionStorage.getItem(SCHEME_CACHE_KEY);
    return isScheme(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

export function applyScheme(scheme: Scheme): void {
  document.documentElement.dataset.scheme = scheme;
  try {
    sessionStorage.setItem(SCHEME_CACHE_KEY, scheme);
  } catch {
    // Storage can be unavailable (private windows, blocked site data). The
    // scheme still applies; only the flash-free reload is lost.
  }
}
